import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    ViewChild,
} from "@angular/core";
import { ITab } from "@shared/models/tab";
import { TabsStateService } from "@shared/services/tabs-state/tabs-state.service";
import { Router } from "@angular/router";
import { ActionItems } from "@layout/static/action-items";
import { Subscription } from "rxjs";
import { Action } from "@shared/models/tab-subject-action";
import { Popover } from "primeng/popover";
import { addTabPopoverPtOptions } from "@layout/components/tabsbar/add-tab-popover-pt-options";
import { Tooltip } from "primeng/tooltip";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { NgClass } from "@angular/common";
import { IActionItem } from "@layout/models/action-item";
import { faXmark, faCirclePlus } from "@fortawesome/pro-solid-svg-icons";

@Component({
    selector: "app-tabsbar",
    templateUrl: "./tabsbar.component.html",
    styleUrl: "./tabsbar.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Tooltip, FaIconComponent, NgClass, Popover],
})
export class TabsbarComponent implements OnInit, OnDestroy {
    public addTabPopoverPtOptions = addTabPopoverPtOptions;
    public faXMark = faXmark;
    public faCirclePlus = faCirclePlus;

    @ViewChild("op") op!: Popover;

    public defaultTabTitle = input<string>("New Tab");
    public defaultTabComponentUrl = input<string>("");
    public maxTabsAllowed = input<number>(10);
    public tabClicked = output<ITab>();

    private tabsService = inject(TabsStateService);
    private router = inject(Router);

    public tabs = this.tabsService.tabs;
    public isNewTabButtonActive = signal(false);
    public actionItems: IActionItem[] = ActionItems;
    private tabActionsSub!: Subscription;

    ngOnInit(): void {
        this.tabActionsSub = this.tabsService.tabActionSubject.subscribe((result) => {
            if (result.action === Action.Add && result.id) {
                const tab = this.tabsService.getTabById(result.id);

                if (!tab) return;

                this.handleTabClicked(tab);
            }
        });
    }

    ngOnDestroy(): void {
        this.tabActionsSub.unsubscribe();
    }

    handleTabClicked(selectedTab: ITab): void {
        this.tabsService.activateTab(selectedTab);
        this.tabClicked.emit(selectedTab);
    }

    isTabActive(tab: ITab): boolean {
        return tab.isActive;
    }

    handleOverlayPanelShown(): void {
        this.isNewTabButtonActive.set(true);
    }

    handleOverlayPanelHidden(): void {
        this.isNewTabButtonActive.set(false);
        this.op.hide();
    }

    handleNewTabOpened(url: string, $event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();

        if (this.tabs().length >= this.maxTabsAllowed()) {
            return;
        }

        const newTab = {
            tabComponentUrl: url,
            tabState: null,
            isActive: true,
        } as ITab;

        this.tabsService.addTab(newTab);

        this.handleOverlayPanelHidden();
    }

    handleTabClosed($event: Event, tabToDelete: ITab): void {
        $event.stopPropagation();

        if (tabToDelete.isActive) {
            if (this.tabs().length == 1) {
                //handle last tab case
                this.router.navigate(["/"]);
            } else {
                //handle any other active tab
                const newActiveTab: ITab = this.tabsService.getNextOrPreviousTab(tabToDelete);
                this.handleTabClicked(newActiveTab);
            }
        }

        this.tabsService.deleteTab(tabToDelete.tabId);
    }
}
